const DISCORD_LINK = "https://discord.gg/qCvxh7ZhSv";

const COMMUNITY_LINK = "https://community.quantumlogicslimited.com";
const COMMUNITY_EXPLORE_LINK = `${COMMUNITY_LINK}/explore`;
const COMMUNITY_REGISTER_LINK = `${COMMUNITY_LINK}/register`;
const COMMUNITY_RULES_LINK = `${COMMUNITY_LINK}/rules`;
const YOUTUBE_LINK = "https://youtube.com/@QuantumLogicsLimited";

const AV_COLORS = [
  "#5865f2",
  "#7289da",
  "#23d18b",
  "#f5c842",
  "#ff6b8a",
  "#00d4ff",
];
const AV_EMOJIS = ["⚡", "🚀", "🛸", "🔮", "🌊", "🎯"];

/* ── About the Quantum Community ─────────────────────────────────────────── */
const COMMUNITY_PILLARS = [
  {
    icon: "🏢",
    title: "Companies, teams & projects",
    text: "Every member lands on a team. Teams roll up into projects, projects into companies, and every company is led by a Commander.",
  },
  {
    icon: "📈",
    title: "Activity that counts",
    text: "Discord messages, GitHub commits and pull requests, lectures, PolyCode and Boolforge XP, shared ideas and stream uploads all turn into quantum points.",
  },
  {
    icon: "🏅",
    title: "Ranks, seasons & medals",
    text: "Climb 15 rank tiers from Novice to Divine, reset with everyone each season, and win Daily, Weekly and Monthly Achiever medals.",
  },
  {
    icon: "💡",
    title: "Ideas board",
    text: "Share an idea, let the community vote and discuss it, and watch Software Engineers close it out as completed or declined.",
  },
  {
    icon: "🎥",
    title: "Weekly streams",
    text: "Captains run team and project meetings every week and publish them to YouTube, so nobody falls behind when they miss a live session.",
  },
  {
    icon: "🌐",
    title: "Public profiles",
    text: "Every member with a linked GitHub gets a public @handle page with their rank, medals and a contribution heatmap across every source.",
  },
];

const COMMUNITY_STEPS = [
  {
    n: "01",
    title: "Join the Discord",
    text: "Pass Quantum Verifier and land in the right channels.",
  },
  {
    n: "02",
    title: "Create your portal account",
    text: "Sign up free on the Quantum Community portal. New accounts start as interns on every training track.",
  },
  {
    n: "03",
    title: "Link GitHub & Discord",
    text: "Your commits, pull requests and messages start counting toward your rank.",
  },
  {
    n: "04",
    title: "Join a team and ship",
    text: "Pick a team, work with your captain, and climb from Team Member to Commander.",
  },
];

/* ── Hierarchy ───────────────────────────────────────────────────────────── */
const ROLE_LADDER = [
  {
    num: "01",
    tone: "teal",
    image: "/community/roles/qubit.png",
    title: "Team Member",
    pill: "Everyone",
    desc: "Everyone starts here. Members cooperate with their Team Captain on objectives and tasks, raise blockers early, and watch the YouTube recording whenever they miss a live session.",
    chips: [
      "Cooperate with captain",
      "Watch if absent",
      "Flag blockers early",
      "Earn quantum points",
    ],
    connector: "reports to",
  },
  {
    num: "02",
    tone: "blue",
    image: "/community/roles/teamCaptain.png",
    title: "Team Captain",
    pill: "Team leadership",
    desc: "The frontline leader of a single team. Team Captains run 3 team meetings every week on their own schedule, keep the roster aligned, and report serious misconduct upward. They are the heartbeat of the team.",
    chips: [
      "Owns 1 team",
      "3 meetings a week",
      "+10 quantum per stream",
      "Reports misconduct",
    ],
    connector: "reports to",
  },
  {
    num: "03",
    tone: "purple",
    image: "/community/roles/projectCaptain.png",
    title: "Project Captain",
    pill: "Cross-team ownership",
    desc: "Project Captains lead 2 to 4 teams, each run by its own Team Captain. They hold 2 to 4 project streams a week, review reports from Team Captains, and are held to Knight tier plus at least 3 streams in the previous week.",
    chips: [
      "Leads 2–4 teams",
      "2–4 streams a week",
      "+15 quantum per stream",
      "Knight tier required",
      "Approved by a Software Engineer",
    ],
    connector: "reports to",
  },
  {
    num: "04",
    tone: "gold",
    image: "/community/roles/commander.png",
    title: "Commander",
    pill: "Strategic leadership",
    desc: "The top of a company. A Commander oversees two Project Captains, finishes one ongoing project assigned by a Software Engineer, and chooses one independent project of their own. They are the final escalation point and the final authority on bans.",
    chips: [
      "Oversees 2 Project Captains",
      "1 ongoing + 1 own project",
      "3 teams for their own project",
      "Final authority",
      "Knight tier required",
    ],
    connector: null,
  },
];

/* ── Commander spotlight ─────────────────────────────────────────────────── */
const COMMANDER_DUTIES = [
  {
    icon: "🧭",
    title: "Oversee 2 Project Captains",
    text: "Keeps both Project Captains aligned on delivery, deadlines and direction across every team in the company.",
  },
  {
    icon: "📌",
    title: "Finish an ongoing project",
    text: "A Software Engineer assigns one ongoing project, and the Commander owns it through to completion.",
  },
  {
    icon: "🚀",
    title: "Launch your own project",
    text: "Chooses one independent project, parent project included, with no Software Engineer approval needed.",
  },
  {
    icon: "🧩",
    title: "Lead 3 allocated teams",
    text: "A Software Engineer allocates 3 teams to the Commander's own project, and the Commander sets its direction, deadlines and structure.",
  },
  {
    icon: "🛡️",
    title: "Final escalation & bans",
    text: "Reports travel Team Captain → Project Captain → Commander. The Commander is the final authority and may ban a user when action is warranted.",
  },
  {
    icon: "✋",
    title: "Approve new Team Captains",
    text: "Commanders approve Team Member → Team Captain promotions for the teams they command and can manage those captains.",
  },
];

const COMMANDER_STANDARDS = [
  "Hold Knight tier or above (3,000 quantum points over the last 365 days).",
  "Below Knight, a red Alerts button stays on the Overview until the standard is met.",
  "Going on leave vacates the seat, and returning takes one from a Commander on warning.",
  "A Software Engineer sits above every Commander and assigns their ongoing project and teams.",
];

const COMPANY_TREE = {
  projects: [
    { label: "Ongoing project", note: "Assigned by a Software Engineer" },
    { label: "Own project", note: "Chosen by the Commander · 3 teams" },
  ],
};

/* ── Weekly rhythm ───────────────────────────────────────────────────────── */
const WEEKLY_FLOW = [
  {
    step: "01",
    tone: "blue",
    title: "Team sync",
    text: "The Team Captain runs up to 3 weekly sessions, live or recorded, covering goals and tasks.",
  },
  {
    step: "02",
    tone: "teal",
    title: "Member catch-up",
    text: "Missed a session? Watch the YouTube upload and stay aligned with the team.",
  },
  {
    step: "03",
    tone: "purple",
    title: "Project stream",
    text: "The Project Captain meets each assigned team 2–4 times a week, aligned with the Commander.",
  },
  {
    step: "04",
    tone: "gold",
    title: "Commander oversight",
    text: "The Commander reviews both Project Captains and drives the ongoing and own projects forward.",
  },
  {
    step: "05",
    tone: "red",
    title: "Escalation",
    text: "Captain reports → Project Captain reviews → Commander decides → ban when necessary.",
  },
];

/* ── Scoring ─────────────────────────────────────────────────────────────── */
const SCORING_ROWS = [
  {
    activity: "Discord message",
    points: "+2",
    note: "First 50 a day count (100 points max). Deleted messages and messages under 3 words earn nothing.",
  },
  {
    activity: "GitHub commit",
    points: "+1",
    note: "First 50 commits a day count. Extra commits still show but score nothing.",
  },
  {
    activity: "GitHub pull request",
    points: "+10 / +5",
    note: "10 points while you are below Knight, 5 once you are Knight or above. Not capped.",
  },
  {
    activity: "PolyCode XP",
    points: "+1 per 5 XP",
    note: "Up to 100 points a day. Link your PolyCode account in Settings.",
  },
  {
    activity: "Boolforge XP",
    points: "+1 per 5 XP",
    note: "Up to 100 points a day. Use the same email as your portal account.",
  },
  {
    activity: "Idea shared",
    points: "+50 / +10",
    note: "Once per day. 50 points below Knight, 10 at Knight or above.",
  },
  {
    activity: "Lecture published",
    points: "+100",
    note: "Lecturers only, for every lecture published.",
  },
  {
    activity: "Captain stream upload",
    points: "+10 / +15",
    note: "Team Captains earn 10 per stream and Project Captains earn 15.",
  },
];

/* ── Ranks ───────────────────────────────────────────────────────────────── */
// Mirrors the portal's ladder: three 1,000-point tiers, eleven tiers of three
// 500-point divisions (1,500 points each) from Knight, then Divine.
const RANK_NAMES = [
  ["Novice", "novice"],
  ["Apprentice", "apprentice"],
  ["Adept", "adept"],
  ["Knight", "knight"],
  ["Elite", "elite"],
  ["Champion", "champion"],
  ["Hero", "hero"],
  ["Guardian", "guardian"],
  ["Warlord", "warlord"],
  ["Master", "master"],
  ["Grandmaster", "grandmaster"],
  ["Legend", "legend"],
  ["Mythic", "mythic"],
  ["Ascendant", "ascendant"],
  ["Divine", "divine"],
];
const RANK_ACCENTS = [
  "#94a3b8",
  "#4ade80",
  "#34d399",
  "#38bdf8",
  "#60a5fa",
  "#818cf8",
  "#a78bfa",
  "#c084fc",
  "#e879f9",
  "#f472b6",
  "#fb7185",
  "#fb923c",
  "#f97316",
  "#fbbf24",
  "#f59e0b",
];
const KNIGHT_INDEX = 3;
const DIVINE_INDEX = RANK_NAMES.length - 1;

const RANK_TIERS = RANK_NAMES.map(([name, file], i) => {
  const divided = i >= KNIGHT_INDEX && i < DIVINE_INDEX;
  const floor =
    i < KNIGHT_INDEX ? i * 1000 : 3000 + (i - KNIGHT_INDEX) * 1500;
  const span = i < KNIGHT_INDEX ? 1000 : divided ? 1500 : null;
  return {
    name,
    level: i + 1,
    image: `/community/tiers/${file}.png`,
    accent: RANK_ACCENTS[i],
    floor,
    ceiling: span === null ? null : floor + span - 1,
    divided,
  };
});

const SEASONS = [
  { months: "Jan – Mar", key: "Q1" },
  { months: "Apr – Jun", key: "Q2" },
  { months: "Jul – Sep", key: "Q3" },
  { months: "Oct – Dec", key: "Q4" },
];

/* ── Medals ──────────────────────────────────────────────────────────────── */
const MEDAL_PERIODS = [
  {
    period: "Every day",
    image: "/community/awards/daily.png",
    name: "Daily Achiever",
    points: 50,
    how: "Top the Most active board for a finished Pakistan-time day.",
    extra: [
      { name: "GitHub Daily Achiever", points: 20, how: "most commits" },
      { name: "PR Titan Bronze", points: 30, how: "most pull requests" },
    ],
  },
  {
    period: "Every week",
    image: "/community/awards/weekly.png",
    name: "Weekly Achiever",
    points: 100,
    how: "Top the board for a finished Monday–Sunday week.",
    extra: [
      { name: "GitHub Champ", points: 40, how: "most commits" },
      { name: "PR Titan Silver", points: 60, how: "most pull requests" },
    ],
  },
  {
    period: "Every month",
    image: "/community/awards/monthly.png",
    name: "Monthly Achiever",
    points: 400,
    how: "Top the board for a finished calendar month.",
    extra: [
      { name: "GitHub Geek", points: 160, how: "most commits" },
      { name: "PR Titan Gold", points: 240, how: "most pull requests" },
    ],
  },
];

export {
  AV_COLORS,
  AV_EMOJIS,
  DISCORD_LINK,
  COMMUNITY_LINK,
  COMMUNITY_EXPLORE_LINK,
  COMMUNITY_REGISTER_LINK,
  COMMUNITY_RULES_LINK,
  YOUTUBE_LINK,
  COMMUNITY_PILLARS,
  COMMUNITY_STEPS,
  ROLE_LADDER,
  COMMANDER_DUTIES,
  COMMANDER_STANDARDS,
  COMPANY_TREE,
  WEEKLY_FLOW,
  SCORING_ROWS,
  RANK_TIERS,
  SEASONS,
  MEDAL_PERIODS,
};
